import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Brand } from '../types'
import { Icons } from './icons'
import { Field } from './common/Field'
import { SectionCard } from './common/SectionCard'
import { ImageDropzone } from './common/ImageDropzone'
import { BrandPreview } from './preview/BrandPreview'
import { brandInitial } from '../utils/brand'
import { Checkbox } from '@/ui/checkbox'

export interface BrandEditorViewProps {
  brand: Brand
  isNew: boolean
  /** Domain-Verwaltung ist admin-only — nur dann den „Domains verwalten"-Link zeigen. */
  isAdmin: boolean
  scopeHint: string
  showBack: boolean
  backTo?: string
  saving: boolean
  onSave: (payload: Brand) => void
}

/** Kontrakt-Pfade für die öffentliche Feld-Allowlist (vgl. Backend `CONTRACT_FIELDS`, Task 2/13). */
const CONTRACT_FIELDS: { path: string; label: string }[] = [
  { path: 'vcard.firstName', label: 'Vorname' },
  { path: 'vcard.lastName', label: 'Nachname' },
  { path: 'vcard.role', label: 'Rolle/Position' },
  { path: 'vcard.profileImage', label: 'Profilbild' },
  { path: 'vcard.email', label: 'E-Mail' },
  { path: 'vcard.phone', label: 'Telefon' },
  { path: 'vcard.mobile', label: 'Mobil' },
  { path: 'vcard.whatsapp', label: 'WhatsApp' },
  { path: 'vcard.facebook', label: 'Facebook' },
  { path: 'vcard.linkedin', label: 'LinkedIn' },
  { path: 'vcard.website', label: 'Website' },
  { path: 'vcard.googleBusinessMessage', label: 'Google Business Message' },
  { path: 'vcard.team.name', label: 'Team-Name' },
  { path: 'vcard.team.logo', label: 'Team-Logo' },
  { path: 'vcard.team.address', label: 'Team-Adresse' },
  { path: 'vcard.team.openingHours', label: 'Öffnungszeiten' },
  { path: 'brand.name', label: 'Brand-Name' },
  { path: 'brand.primaryColor', label: 'Brand-Farbe' },
  { path: 'brand.logo', label: 'Brand-Logo' },
  { path: 'brand.signet', label: 'Signet-Bild' },
  { path: 'brand.claim', label: 'Claim' },
]
const ALL_FIELD_PATHS = CONTRACT_FIELDS.map((f) => f.path)

interface BrandFormState {
  brandName: string
  brandLink: string
  primaryColor: string
  sigent: string
  signetImage: string
  brandDescription: string
  logo: string
  brandId: string
  externalID: string
  brandKey: string
}

function toFormState(brand: Brand): BrandFormState {
  return {
    brandName: brand.brandName ?? '',
    brandLink: brand.brandLink ?? '',
    primaryColor: brand.primaryColor || '#4A6CF7',
    sigent: brand.sigent ?? '',
    signetImage: brand.signetImage ?? '',
    brandDescription: brand.brandDescription ?? '',
    logo: brand.logo ?? '',
    brandId: brand.brandId ?? '',
    externalID: brand.externalID ?? '',
    brandKey: brand.brandKey ?? '',
  }
}

/**
 * Präsentativer Editor-Körper eines Brand-Profils: gruppierte Sektionen (links)
 * plus Live-Vorschau (rechts) und Action-Bar — BMC-Designsystem `console.css`.
 */
export function BrandEditorView({
  brand,
  isNew,
  isAdmin,
  scopeHint,
  showBack,
  backTo,
  saving,
  onSave,
}: BrandEditorViewProps) {
  const navigate = useNavigate()
  const [values, setValues] = useState<BrandFormState>(() => toFormState(brand))
  const [allowlist, setAllowlist] = useState<string[]>(
    brand.fieldAllowlist?.length ? brand.fieldAllowlist : ALL_FIELD_PATHS,
  )

  const toggleField = (path: string): void =>
    setAllowlist((current) => (current.includes(path) ? current.filter((p) => p !== path) : [...current, path]))

  const setField = (field: keyof BrandFormState, value: string): void => {
    setValues((prev) => ({ ...prev, [field]: value }))
  }

  const bind = (field: keyof BrandFormState): { value: string; onChange: (v: string) => void } => ({
    value: values[field],
    onChange: (v) => setField(field, v),
  })

  const previewBrand: Brand = { ...values }

  const handleDiscard = (): void => {
    if (showBack && backTo) navigate(backTo)
    else setValues(toFormState(brand))
  }

  const handleSave = (): void => {
    const fieldAllowlist = allowlist.length === ALL_FIELD_PATHS.length ? null : allowlist
    onSave({
      ...values,
      brandKey: values.brandKey,
      fieldAllowlist,
    })
  }

  return (
    <>
      <div className="mb-5 flex items-center gap-3.5">
        <span
          className="grid size-11 flex-none place-items-center rounded-xl text-lg font-extrabold text-white"
          style={{ background: values.primaryColor }}
        >
          {brandInitial({ brandKey: values.brandKey, brandName: values.brandName })}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-2xl font-semibold leading-tight" style={{ color: 'var(--bmc-fg)' }}>
            {values.brandName || 'Neue Brand'}
          </h1>
          <div className="fhint">Brand-Key: {values.brandKey || '–'}</div>
        </div>
        {isAdmin && !isNew && values.brandKey && (
          <button
            type="button"
            className="btn outline sm"
            onClick={() => navigate(`/admin/brands/${values.brandKey}/domains`)}
          >
            {Icons.domains} Domains verwalten
          </button>
        )}
        {showBack && backTo && (
          <button type="button" className="btn ghost sm" onClick={() => navigate(backTo)}>
            {Icons.back} Zurück
          </button>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-5">
          <SectionCard icon={Icons.brand} title="Marke">
            <div className="flex flex-col gap-4">
              <Field id="brandName" label="Markenname" icon={Icons.brand} {...bind('brandName')} />
              <Field id="brandLink" label="Website" type="url" icon={Icons.web} {...bind('brandLink')} />
              <div className="field">
                <label className="flabel" htmlFor="f_primaryColor">
                  Primärfarbe
                </label>
                <input
                  id="f_primaryColor"
                  type="color"
                  value={values.primaryColor}
                  onChange={(e) => setField('primaryColor', e.target.value)}
                  className="h-10 w-16 cursor-pointer p-1"
                  style={{ borderRadius: 'var(--r-sm)', border: '1px solid var(--bmc-line-2)', background: 'var(--bmc-bg)' }}
                />
              </div>
              <Field id="sigent" label="Signet / Claim" optional {...bind('sigent')} />
              <ImageDropzone
                value={values.signetImage}
                onChange={(v) => setField('signetImage', v)}
                label="Signet (Bild)"
                hint="optional · PNG, JPG oder SVG · wird als base64 gespeichert"
              />
              <div className="field">
                <label className="flabel" htmlFor="f_brandDescription">
                  Beschreibung <span className="opt">(optional)</span>
                </label>
                <textarea
                  id="f_brandDescription"
                  value={values.brandDescription}
                  onChange={(e) => setField('brandDescription', e.target.value)}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={Icons.eye} title="Logo">
            <ImageDropzone
              value={values.logo}
              onChange={(v) => setField('logo', v)}
              label="Marken-Logo"
              hint="PNG, JPG oder SVG · SVG bleibt gestochen scharf · wird als base64 gespeichert"
            />
          </SectionCard>

          <SectionCard icon={Icons.id} title="Referenzen">
            <div className="flex flex-col gap-4">
              <Field id="brandId" label="Brand-ID" optional {...bind('brandId')} />
              <Field id="externalID" label="Externe ID" optional {...bind('externalID')} />
              {isNew ? (
                <Field id="brandKey" label="Brand-Key" {...bind('brandKey')} />
              ) : (
                <div className="field">
                  <label className="flabel" htmlFor="f_brandKey">
                    Brand-Key <span className="opt">(nicht änderbar)</span>
                  </label>
                  <input className="inp" id="f_brandKey" value={values.brandKey} disabled readOnly />
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard icon={Icons.toggle} title="Öffentliche Felder">
            <div className="flex flex-col gap-3">
              <p className="fhint">
                Steuert, welche Felder der öffentliche Viewer erhält. Alles angehakt = globaler Standard.
              </p>
              <div className="grid gap-1 sm:grid-cols-2">
                {CONTRACT_FIELDS.map((field) => {
                  const inputId = `fld_${field.path}`
                  return (
                    <div key={field.path} className="checkrow">
                      <Checkbox
                        id={inputId}
                        checked={allowlist.includes(field.path)}
                        onCheckedChange={() => toggleField(field.path)}
                        aria-label={field.label}
                      />
                      <label htmlFor={inputId} className="cursor-pointer">
                        {field.label}
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          </SectionCard>

          <div
            className="flex items-center gap-3 p-4"
            style={{
              borderRadius: 'var(--r)',
              border: '1px solid var(--bmc-line)',
              background: 'var(--bmc-panel)',
            }}
          >
            <span className="fhint">{scopeHint}</span>
            <div className="flex-1" />
            <button type="button" className="btn outline" onClick={handleDiscard}>
              Verwerfen
            </button>
            <button type="button" className="btn primary" disabled={saving} onClick={handleSave}>
              Speichern
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="eyebrow">Vorschau</div>
          <BrandPreview data={previewBrand} />
        </div>
      </div>
    </>
  )
}
